/**
 * Asset Table Filters Component
 *
 * Provides advanced filtering UI for the asset table.
 * Includes dropdown filters for status, attack surface, source, and other criteria.
 * Renders as a React Fragment for use inside TableControlsContainer.
 * Search bar is rendered separately in TableControlsContainer.filtersRight.
 * Automatically optimized via React Compiler.
 */

import React, { Dispatch, SetStateAction, useState } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { FilterOption, GroupOption } from '@/hooks/api/useGetAssetFilters';
import type { Filters } from '@/hooks/useAssets';
import { getFilterDropdownWidth } from '@/sections/asset/config';
import { useAuth } from '@/state/auth';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { omit } from '@/utils/lodash.util';
import {
  collapseOriginValues,
  getConsolidatedOriginIds,
} from '@/utils/originDisplay.util';

interface AssetTableFiltersProps {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  /** When provided, only show filter dropdowns for these keys (e.g., ['status', 'tags', 'attackSurface']) */
  visibleFilters?: string[];
}

/**
 * Filter controls for asset table
 *
 * Provides:
 * - Multi-select dropdown filters (status, attack surface, source, etc.)
 * - Clear all filters button
 *
 * Returns a React Fragment to be composed inside TableControlsContainer.
 * Search bar is handled separately via TableControlsContainer.filtersRight.
 *
 * React Compiler automatically optimizes event handlers and computed values.
 *
 * @param filters - Current filter state
 * @param setFilters - Function to update filter state
 */
export const AssetTableFilters: React.FC<AssetTableFiltersProps> = ({
  filters,
  setFilters,
  visibleFilters,
}) => {
  'use memo';

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const {
    filterOptions,
    sortedOptions,
    status: filterOptionsStatus,
  } = useEntityFilters('Asset', 'asset-table');
  const { isPraetorianUser, isDemoOrg } = useAuth();

  const isDropdownFiltersApplied =
    filters.advancedFilters &&
    Object.values(filters.advancedFilters).find(value =>
      Boolean(value?.length)
    );

  const handleDropdownStateChange = (isOpen: boolean, id: string) => {
    if (isOpen) {
      setOpenDropdownId(id);
    } else if (openDropdownId === id) {
      setOpenDropdownId(null);
    }
  };

  return (
    <>
      {sortedOptions.map((option, index) => {
        const key = option.value as string;

        // When visibleFilters is provided, only show filters in that list
        if (visibleFilters && !visibleFilters.includes(key)) {
          return null;
        }

        const filterOption =
          filterOptions &&
          typeof filterOptions === 'object' &&
          !Array.isArray(filterOptions)
            ? filterOptions[key]
            : null;
        if (!filterOption) return null;

        return (
          <FilterDropdown
            key={index}
            id={key}
            isLoading={filterOptionsStatus === 'pending'}
            label={option.label}
            options={filterOption as (FilterOption | GroupOption)[]}
            isPraetorianUser={isPraetorianUser && !isDemoOrg}
            selectedValues={
              key === 'origins'
                ? collapseOriginValues(
                    filters.advancedFilters?.[
                      key as keyof typeof filters.advancedFilters
                    ] || []
                  )
                : filters.advancedFilters?.[
                    key as keyof typeof filters.advancedFilters
                  ] || []
            }
            onApply={selected => {
              // Extract values from selected options
              let values = selected.map(
                option => option.value?.toString() || ''
              );

              // Expand consolidated values for origins filter (NSA CSaaS, CISA CH)
              if (key === 'origins') {
                values = values.flatMap(value => {
                  const expanded = getConsolidatedOriginIds(value);
                  return expanded || [value]; // Use expanded IDs or original value
                });
              }

              // Update filter state with expanded values
              setFilters(prev => ({
                ...prev,
                advancedFilters:
                  values.length > 0
                    ? { ...prev.advancedFilters, [key]: values }
                    : omit(prev.advancedFilters, key),
              }));
            }}
            onClear={() => {
              setFilters(prev => ({
                ...prev,
                advancedFilters: omit(prev.advancedFilters, key),
              }));
            }}
            isLoadingOptions={filterOptionsStatus === 'pending'}
            onDropdownStateChange={handleDropdownStateChange}
            width={getFilterDropdownWidth(key)}
          />
        );
      })}
      {isDropdownFiltersApplied && (
        <ClearFiltersButton
          onClick={() =>
            setFilters(filters => {
              return {
                ...filters,
                advancedFilters: {},
              };
            })
          }
        />
      )}
    </>
  );
};
